/**
 * Final (non-streaming) HTML preview — a genuine document load via `srcDoc`.
 *
 * `HtmlPreviewFrame` is used while a create/update is actively streaming: it
 * keeps one stable iframe mounted and patches the body in place via
 * morphdom so token-by-token updates don't flicker or re-fetch Tailwind.
 * That mechanism has a real limitation, not a stylistic one — morphdom
 * (like a plain `innerHTML` assignment) never executes `<script>` tags it
 * inserts. A model-authored chart built with a JS library would sit there
 * as a permanently blank canvas with no error anywhere.
 *
 * Once generation is complete there's no more streaming to protect against
 * flicker for, so this component takes over: it sets `srcDoc` straight to
 * the artifact's finished HTML, which is a genuine page load — scripts run
 * exactly as they would in a normal browser tab. The cost is one full
 * reload when the canvas switches from the live view to this one; that's a
 * reasonable trade for "the chart the model built actually renders."
 *
 * @module components/artifacts/html-static-frame
 */

import { hashFrameContent } from "@/lib/artifacts/frame-key";

interface HtmlStaticFrameProps {
  /** Complete HTML document to render. */
  html: string;
  /** Used as the iframe `title` for accessibility tooling. */
  title: string;
  /**
   * Bump to force a hard reload even when `html` is unchanged — backs the
   * canvas toolbar's "Refresh preview" button, letting the user manually
   * re-run scripts if something didn't settle right the first time.
   */
  reloadKey: number;
  /** Fired on the iframe's native `load` event once the document (and any scripts) have finished. */
  onReady?: () => void;
}

/**
 * Static HTML preview. Re-mounted (full reload) whenever `html` or
 * `reloadKey` changes — there is no in-place patching here, so every
 * change is a fresh document parse and any scripts run from scratch.
 */
export function HtmlStaticFrame({ html, title, reloadKey, onReady }: HtmlStaticFrameProps) {
  return (
    <iframe
      // Keyed on the content as well as `reloadKey` — keying on `reloadKey`
      // alone made this claim false for the ordinary case: a new `html` with
      // an unchanged `reloadKey` reused the element and mutated `srcdoc` in
      // place, which is exactly the update Chromium may not re-navigate.
      key={`${reloadKey}:${hashFrameContent(html)}`}
      // Same sandbox posture as the live preview: scripts run, but the
      // iframe has no same-origin access to the host app — no cookies, no
      // parent DOM, no localStorage. `srcDoc` alone (without
      // `allow-same-origin`) keeps that isolation even for a full load.
      sandbox="allow-scripts"
      srcDoc={html}
      title={title || "HTML artifact"}
      onLoad={onReady}
      className="h-full w-full bg-white"
    />
  );
}
